/**
 * "Suggested priority" on the propose form — the proposer's AAA/A/B/C, filed in the proposals
 * table's `suggested_priority`. Never `Hiring Manager Priority Input`: that is a lookup, and
 * writing it broke proposing (Sep 30 2026).
 *
 * A SUGGESTION, and labelled as one everywhere it appears: the rating Role Prioritisation counts is
 * `Approved Priority Rating`, which only the CEO sets, on Role Approvals (see
 * `proxy-server/lib/roleApprovalPriority.js`). His approval of the role confirms this suggestion
 * unless he picks another, so a good suggestion here is usually the final rating.
 *
 * Optional, because a proposer who cannot place the role should leave it to him rather than guess.
 *
 * The link opens the Company View of Role Prioritisation in a NEW tab: it is context for the
 * choice, and navigating this tab away would throw the half-filled form away with it.
 */
import { ExternalLink } from 'lucide-react';
import { FIELD_CLASS } from './proposeRole';
import { Field } from './proposeRoleUi';

export const ROLE_PRIORITISATION_COMPANY_VIEW = '/ta-cockpit/role-prioritisation/score';

/** The four ratings, highest first — used ONLY when the proxy predates `options.priorityRating`. */
const FALLBACK_RATINGS = ['AAA', 'A', 'B', 'C'];

export function PriorityField({
  value,
  onChange,
  options,
  disabled,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[] | undefined;
  disabled: boolean;
  error?: string;
}) {
  // An EMPTY list means the proxy offers no priority at all, so there is no field to show. Only
  // `undefined` — a proxy that predates the option — gets the fallback.
  if (options && options.length === 0) return null;
  const choices = options ?? FALLBACK_RATINGS;
  return (
    <Field label="Suggested priority" htmlFor="priorityRating" error={error}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <select
          id="priorityRating"
          aria-describedby="priorityRating-help"
          className={`${FIELD_CLASS} !w-40`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">—</option>
          {choices.map((rating) => (
            <option key={rating} value={rating}>
              {rating}
            </option>
          ))}
        </select>
        <a
          href={ROLE_PRIORITISATION_COMPANY_VIEW}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-blue-700 underline underline-offset-2 hover:text-blue-900"
        >
          See how open roles are rated today
          <ExternalLink className="h-3 w-3" aria-hidden />
          <span className="sr-only">(opens in a new tab)</span>
        </a>
      </div>
      <p id="priorityRating-help" className="mt-1 text-xs text-zinc-500">
        Where this role should rank against the roles already open. The CEO approves or changes it.
      </p>
    </Field>
  );
}
